import type { CogoDocumentNodeKind } from "./document-operation";

export const COGO_DOCUMENT_GRAPH_SCHEMA_VERSION =
  "cogo.document-graph.v1" as const;

export type CogoDocumentNodeStatus = "active" | "proposed";

export interface CogoDocumentNode {
  nodeId: string;
  kind: CogoDocumentNodeKind;
  parentId: string | null;
  childIds: string[];
  pageId: string | null;
  title?: string;
  text?: string;
  attributes: Record<string, unknown>;
  sourceLocatorIds: string[];
  status: CogoDocumentNodeStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CogoDocumentGraph {
  schemaVersion: typeof COGO_DOCUMENT_GRAPH_SCHEMA_VERSION;
  rootId: string;
  pageOrder: string[];
  nodes: Record<string, CogoDocumentNode>;
}

export interface CogoDocumentPageSummary {
  pageId: string;
  pageIndex: number;
  title: string;
  kind: "page" | "slide";
  status: CogoDocumentNodeStatus;
  nodeIds: string[];
  text: string;
}

function cloneGraph(graph: CogoDocumentGraph): CogoDocumentGraph {
  return structuredClone(graph);
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function slug(value: string): string {
  return (
    value
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "page"
  );
}

function nextNodeId(graph: CogoDocumentGraph, base: string): string {
  if (!graph.nodes[base]) return base;
  let suffix = 2;
  while (graph.nodes[`${base}-${suffix}`]) suffix += 1;
  return `${base}-${suffix}`;
}

function createNode(input: {
  nodeId: string;
  kind: CogoDocumentNodeKind;
  parentId: string | null;
  pageId: string | null;
  title?: string;
  text?: string;
  status?: CogoDocumentNodeStatus;
  sourceLocatorIds?: string[];
  attributes?: Record<string, unknown>;
  now: string;
}): CogoDocumentNode {
  return {
    nodeId: input.nodeId,
    kind: input.kind,
    parentId: input.parentId,
    childIds: [],
    pageId: input.pageId,
    title: input.title,
    text: input.text,
    attributes: input.attributes ?? {},
    sourceLocatorIds: unique(input.sourceLocatorIds ?? []),
    status: input.status ?? "active",
    createdAt: input.now,
    updatedAt: input.now,
  };
}

export function createCogoDocumentGraph(input: {
  documentId: string;
  title: string;
  pageTitle?: string;
  createdAt?: string;
}): CogoDocumentGraph {
  const now = input.createdAt ?? new Date().toISOString();
  const rootId = input.documentId;
  const pageId = "page.1";
  const sectionId = "section.summary";
  const headingId = "section.summary.heading";
  const paragraphId = "page.1.paragraph.1";
  const nodes: Record<string, CogoDocumentNode> = {
    [rootId]: createNode({
      nodeId: rootId,
      kind: "document",
      parentId: null,
      pageId: null,
      title: input.title,
      now,
    }),
    [pageId]: createNode({
      nodeId: pageId,
      kind: "page",
      parentId: rootId,
      pageId,
      title: input.pageTitle ?? "Page 1",
      now,
    }),
    [sectionId]: createNode({
      nodeId: sectionId,
      kind: "section",
      parentId: pageId,
      pageId,
      title: "Summary",
      now,
    }),
    [headingId]: createNode({
      nodeId: headingId,
      kind: "heading",
      parentId: sectionId,
      pageId,
      text: input.title,
      now,
    }),
    [paragraphId]: createNode({
      nodeId: paragraphId,
      kind: "paragraph",
      parentId: sectionId,
      pageId,
      text: "",
      now,
    }),
  };
  nodes[rootId].childIds = [pageId];
  nodes[pageId].childIds = [sectionId];
  nodes[sectionId].childIds = [headingId, paragraphId];
  return {
    schemaVersion: COGO_DOCUMENT_GRAPH_SCHEMA_VERSION,
    rootId,
    pageOrder: [pageId],
    nodes,
  };
}

export function validateCogoDocumentGraph(graph: CogoDocumentGraph): string[] {
  const errors: string[] = [];
  const root = graph.nodes[graph.rootId];
  if (!root || root.kind !== "document" || root.parentId !== null) {
    errors.push("Document graph root must be a parentless document node.");
  }
  if (unique(graph.pageOrder).length !== graph.pageOrder.length) {
    errors.push("Document graph pageOrder contains duplicate page ids.");
  }
  for (const pageId of graph.pageOrder) {
    const page = graph.nodes[pageId];
    if (!page || !["page", "slide"].includes(page.kind)) {
      errors.push(`Document graph pageOrder references an invalid page: ${pageId}`);
    } else if (page.parentId !== graph.rootId || page.pageId !== pageId) {
      errors.push(`Document page ${pageId} is not attached to the graph root.`);
    }
  }
  for (const node of Object.values(graph.nodes)) {
    if (unique(node.childIds).length !== node.childIds.length) {
      errors.push(`Document node ${node.nodeId} contains duplicate children.`);
    }
    if (node.parentId) {
      const parent = graph.nodes[node.parentId];
      if (!parent || !parent.childIds.includes(node.nodeId)) {
        errors.push(`Document node ${node.nodeId} has a broken parent reference.`);
      }
    }
    for (const childId of node.childIds) {
      const child = graph.nodes[childId];
      if (!child || child.parentId !== node.nodeId) {
        errors.push(`Document node ${node.nodeId} has a broken child reference: ${childId}`);
      }
    }
    if (node.pageId && !graph.pageOrder.includes(node.pageId)) {
      errors.push(`Document node ${node.nodeId} references an unknown page: ${node.pageId}`);
    }
  }
  const visited = new Set<string>();
  const active = new Set<string>();
  const visit = (nodeId: string) => {
    if (active.has(nodeId)) {
      errors.push(`Document graph contains a cycle at ${nodeId}.`);
      return;
    }
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    active.add(nodeId);
    for (const childId of graph.nodes[nodeId]?.childIds ?? []) visit(childId);
    active.delete(nodeId);
  };
  if (root) visit(graph.rootId);
  for (const nodeId of Object.keys(graph.nodes)) {
    if (!visited.has(nodeId)) errors.push(`Document graph contains an orphan node: ${nodeId}`);
  }
  return unique(errors);
}

export function isCogoDocumentGraph(value: unknown): value is CogoDocumentGraph {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const graph = value as CogoDocumentGraph;
  return (
    graph.schemaVersion === COGO_DOCUMENT_GRAPH_SCHEMA_VERSION &&
    typeof graph.rootId === "string" &&
    Array.isArray(graph.pageOrder) &&
    Boolean(graph.nodes) &&
    typeof graph.nodes === "object" &&
    validateCogoDocumentGraph(graph).length === 0
  );
}

function assertValid(graph: CogoDocumentGraph): CogoDocumentGraph {
  const errors = validateCogoDocumentGraph(graph);
  if (errors.length > 0) throw new Error(errors.join(" "));
  return graph;
}

function subtreeIds(graph: CogoDocumentGraph, nodeId: string): string[] {
  const node = graph.nodes[nodeId];
  if (!node) return [];
  return [nodeId, ...node.childIds.flatMap((childId) => subtreeIds(graph, childId))];
}

export function listCogoDocumentPages(
  graph: CogoDocumentGraph,
): CogoDocumentPageSummary[] {
  return graph.pageOrder.map((pageId, pageIndex) => {
    const page = graph.nodes[pageId];
    const nodeIds = subtreeIds(graph, pageId);
    return {
      pageId,
      pageIndex: pageIndex + 1,
      title: page.title ?? `Page ${pageIndex + 1}`,
      kind: page.kind === "slide" ? "slide" : "page",
      status: page.status,
      nodeIds,
      text: nodeIds
        .flatMap((nodeId) => graph.nodes[nodeId]?.text ?? [])
        .filter(Boolean)
        .join("\n"),
    };
  });
}

export function pageIdForDocumentNode(
  graph: CogoDocumentGraph,
  nodeId: string,
): string | null {
  return graph.nodes[nodeId]?.pageId ?? null;
}

export function createCogoDocumentPage(input: {
  graph: CogoDocumentGraph;
  title: string;
  pageId?: string;
  kind?: "page" | "slide";
  text?: string;
  toIndex?: number;
  status?: CogoDocumentNodeStatus;
  sourceLocatorIds?: string[];
  now?: string;
}): CogoDocumentGraph {
  const graph = cloneGraph(input.graph);
  const now = input.now ?? new Date().toISOString();
  const kind = input.kind ?? "page";
  const pageId = nextNodeId(
    graph,
    input.pageId?.trim() || `${kind}.${slug(input.title)}`,
  );
  const contentId = nextNodeId(graph, `${pageId}.paragraph.1`);
  graph.nodes[pageId] = createNode({
    nodeId: pageId,
    kind,
    parentId: graph.rootId,
    pageId,
    title: input.title,
    status: input.status,
    sourceLocatorIds: input.sourceLocatorIds,
    now,
  });
  graph.nodes[contentId] = createNode({
    nodeId: contentId,
    kind: kind === "slide" ? "slide-object" : "paragraph",
    parentId: pageId,
    pageId,
    text: input.text ?? "",
    status: input.status,
    sourceLocatorIds: input.sourceLocatorIds,
    now,
  });
  graph.nodes[pageId].childIds = [contentId];
  const toIndex =
    typeof input.toIndex === "number"
      ? Math.max(0, Math.min(graph.pageOrder.length, Math.trunc(input.toIndex)))
      : graph.pageOrder.length;
  graph.pageOrder.splice(toIndex, 0, pageId);
  graph.nodes[graph.rootId].childIds = [...graph.pageOrder];
  return assertValid(graph);
}

export function updateCogoDocumentNode(input: {
  graph: CogoDocumentGraph;
  nodeId: string;
  title?: string;
  text?: string;
  attributes?: Record<string, unknown>;
  status?: CogoDocumentNodeStatus;
  sourceLocatorIds?: string[];
  now?: string;
}): CogoDocumentGraph {
  const graph = cloneGraph(input.graph);
  const node = graph.nodes[input.nodeId];
  if (!node) throw new Error(`Document node "${input.nodeId}" was not found.`);
  if (input.title !== undefined) node.title = input.title;
  if (input.text !== undefined) node.text = input.text;
  if (input.attributes) node.attributes = { ...node.attributes, ...input.attributes };
  if (input.status) node.status = input.status;
  if (input.sourceLocatorIds) {
    node.sourceLocatorIds = unique([...node.sourceLocatorIds, ...input.sourceLocatorIds]);
  }
  node.updatedAt = input.now ?? new Date().toISOString();
  return assertValid(graph);
}

export function moveCogoDocumentPage(input: {
  graph: CogoDocumentGraph;
  pageId: string;
  toIndex: number;
}): CogoDocumentGraph {
  const graph = cloneGraph(input.graph);
  const fromIndex = graph.pageOrder.indexOf(input.pageId);
  if (fromIndex < 0) throw new Error(`Document page "${input.pageId}" was not found.`);
  const toIndex = Math.max(0, Math.min(graph.pageOrder.length - 1, Math.trunc(input.toIndex)));
  graph.pageOrder.splice(fromIndex, 1);
  graph.pageOrder.splice(toIndex, 0, input.pageId);
  graph.nodes[graph.rootId].childIds = [...graph.pageOrder];
  return assertValid(graph);
}

export function deleteCogoDocumentPage(input: {
  graph: CogoDocumentGraph;
  pageId: string;
}): CogoDocumentGraph {
  if (input.graph.pageOrder.length <= 1) {
    throw new Error("A document workspace must keep at least one page.");
  }
  const graph = cloneGraph(input.graph);
  if (!graph.pageOrder.includes(input.pageId)) {
    throw new Error(`Document page "${input.pageId}" was not found.`);
  }
  for (const nodeId of subtreeIds(graph, input.pageId)) delete graph.nodes[nodeId];
  graph.pageOrder = graph.pageOrder.filter((pageId) => pageId !== input.pageId);
  graph.nodes[graph.rootId].childIds = [...graph.pageOrder];
  return assertValid(graph);
}

function inferredPageRef(nodeId: string): { pageId: string; index: number; kind: "page" | "slide" } | null {
  const match = nodeId.match(/(?:^|[._-])(page|slide)[._-]?(\d+)(?:$|[._-])/i);
  if (!match) return null;
  const index = Number.parseInt(match[2], 10);
  if (!Number.isFinite(index) || index < 1) return null;
  const kind = match[1].toLowerCase() === "slide" ? "slide" : "page";
  return { pageId: `${kind}.${index}`, index, kind };
}

export function materializeCogoDocumentAnchor(input: {
  graph: CogoDocumentGraph;
  nodeId: string;
  nodeKind: CogoDocumentNodeKind;
  sourceLocatorIds?: string[];
  status?: CogoDocumentNodeStatus;
  now?: string;
}): CogoDocumentGraph {
  if (input.graph.nodes[input.nodeId]) return cloneGraph(input.graph);
  let graph = cloneGraph(input.graph);
  const inferred = inferredPageRef(input.nodeId);
  const pageId = inferred?.pageId ?? graph.pageOrder[0];
  if (!graph.nodes[pageId]) {
    graph = createCogoDocumentPage({
      graph,
      title: inferred?.kind === "slide" ? `Slide ${inferred.index}` : `Page ${inferred?.index ?? graph.pageOrder.length + 1}`,
      pageId,
      kind: inferred?.kind,
      toIndex: inferred ? inferred.index - 1 : undefined,
      status: input.status ?? "proposed",
      sourceLocatorIds: input.sourceLocatorIds,
      now: input.now,
    });
  }
  if (graph.nodes[input.nodeId]) {
    return updateCogoDocumentNode({
      graph,
      nodeId: input.nodeId,
      status: input.status ?? "proposed",
      sourceLocatorIds: input.sourceLocatorIds,
      now: input.now,
    });
  }
  if (input.nodeId === pageId) return graph;
  const page = graph.nodes[pageId];
  const now = input.now ?? new Date().toISOString();
  graph.nodes[input.nodeId] = createNode({
    nodeId: input.nodeId,
    kind: input.nodeKind,
    parentId: pageId,
    pageId,
    status: input.status ?? "proposed",
    sourceLocatorIds: input.sourceLocatorIds,
    now,
  });
  page.childIds.push(input.nodeId);
  page.updatedAt = now;
  return assertValid(graph);
}
